import { Transition, Dialog } from '@headlessui/react';
import { format } from 'date-fns';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { dateSubmitFormat, getCurrentLangFormat } from '../../types/core/date.types';
import Button from '../button/Button';
import { Loader } from '../loader/Loader';
import {
  ColDataType,
  ColFilter,
  CustomButton,
  DensityType,
  TableColumn,
  TableItem,
} from './DataTable.types';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import SelectMultiple from '../select/SelectMultiple';
import { useTranslation } from '../../context/translationContext';
import { searchByQuery } from '../../utils/functions/data.functions';
import useContextMenu from '../../hooks/useContextMenu';
import { paths } from '../../pages/RouterContainer';
import { sessionStorageKeys } from '../../utils/keys/sessionStorageKeys';
import { RowHeading } from '../select/SelectAutocompleteTable';

interface TableProps<T extends TableItem> {
  data: T[] | null;
  columns: TableColumn[];
  idKey?: string; // attribute sended as id to edit handle function
  handleRemove?: (id: string | number) => void;
  handleEdit?: (id: string | number) => void;
  customButtons?: CustomButton[];
  columnFilters?: boolean;
  fulltextSearch?: boolean;
  density?: DensityType;
  editIdKey?: string;
  removeIdKey?: string;
  showContextMenu?: boolean;
  isModalTable?: boolean;
  useSelection?: boolean;
  onSelected?: (val: any) => void;
  aboveRowHeading?: RowHeading[];
}

/**
 *  Component for displaying data in table with defined columns and actions
 */
export default function DataTable<T extends TableItem>({
  data,
  columns,
  idKey = 'id',
  handleRemove,
  handleEdit,
  customButtons,
  columnFilters,
  fulltextSearch,
  density = DensityType.COMPACT,
  editIdKey,
  removeIdKey,
  showContextMenu,
  isModalTable,
  useSelection,
  onSelected,
  aboveRowHeading,
}: TableProps<T>) {
  const { t, lang } = useTranslation();
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<string | number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<any[] | null>(data);
  // filters on columns
  const [colFilters, setColFilters] = useState<ColFilter[]>([]);
  // context menu
  const { openContextMenu /* ContextMenu */ } = useContextMenu();
  const tableRef = useRef<HTMLTableElement | null>(null);

  // table selection
  useEffect(() => {
    if (!useSelection) return;
    setSelectedRowIndex(0);
  }, []);

  useEffect(() => {
    setFilteredData(data);
    // table selection
    if (useSelection) setSelectedRowIndex(0);
  }, [data]);

  /**
   * Handles table row selection by arrows - up/down and enter
   */
  useEffect(() => {
    const handleKeyPress = (event: any) => {
      if (event.key === 'ArrowUp') {
        moveUp();
      } else if (event.key === 'ArrowDown') {
        moveDown();
      } else if (event.key === 'Enter') {
        // prevent default enter action - no next line
        event.preventDefault();
        selectItem(filteredData);
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [selectedRowIndex, filteredData]);

  /**
   * Moves selection up
   */
  const moveUp = () => {
    if (selectedRowIndex === null) return;
    if (selectedRowIndex > 0) {
      setSelectedRowIndex(selectedRowIndex - 1);
      scrollToSelectedRow();
    }
  };

  /**
   * Moves selection down
   */
  const moveDown = () => {
    if (selectedRowIndex === null || !filteredData) return;
    if (selectedRowIndex < filteredData.length - 1) {
      setSelectedRowIndex(selectedRowIndex + 1);
      scrollToSelectedRow();
    }
  };

  /**
   * Selects item in table
   * @param filteredData
   */
  const selectItem = (filteredData: any) => {
    if (selectedRowIndex === null || !onSelected) return;
    onSelected(filteredData[selectedRowIndex]);
  };

  /**
   * Scrolls to selected row in table when using arrow selection
   */
  const scrollToSelectedRow = () => {
    const container = document.getElementById('filter-modal-table');
    const selectedRow = document.getElementById(`row-${selectedRowIndex}`);
    if (container && selectedRow && selectedRowIndex) {
      // 29 is height of row
      container.scrollTop = selectedRowIndex * 29;
    }
  };

  /**
   * Context menu action to open target in new window
   */
  const openPreviewInNewTab = (id: number) => {
    window.open(`/${paths.PDF_VIEW}/${id}`, '_blank');
  };

  /**
   * Define what options should be in context menu
   * currently set to Edit and Custom buttons
   * @param e
   * @param row
   */
  const handleContextMenu = (e: any, row: any) => {
    const contextMenuItems = [
      { label: 'Open in new tab', action: () => openPreviewInNewTab(row.id) },
    ];
    openContextMenu(e, contextMenuItems);
  };

  /**
   * Function to initiate delete dialog
   * @param id
   */
  const removeAction = async (id: string | number) => {
    setSelectedItem(id);
    setDeleteModalVisible(true);
  };

  /**
   * Closing delete modal after delete action finished
   */
  const handleDeleteModalClose = () => {
    setDeleteModalVisible(false);
  };

  /**
   * Formats cell by rule
   * currently only date formats
   * @param data
   * @param col
   */
  const handleFormatCell = (data: any, col: TableColumn) => {
    if (!col.dataType) {
      if (col.translateEnum && data && Object.keys(col.translateEnum).includes(data))
        return t(col.translateEnum[data]);
      return data;
    }
    switch (col.dataType) {
      case ColDataType.DATE:
        return data ? format(new Date(data), getCurrentLangFormat(lang)) : '';
      case ColDataType.DATE_TIME:
        return data ? format(new Date(data), dateSubmitFormat) : '';
    }
  };

  /**
   * Open column filter window
   */
  const showFilters = (column: string) => {
    setFiltersOpen(!filtersOpen);
    return column;
  };

  /**
   * Filter table by selected filters
   */
  const applyFilters = (data: any, col: string, filters: string[], isInitial?: boolean) => {
    if (filters.length === 0) return setFilteredData(data);
    // filter for column exists?
    const colIndex = colFilters.findIndex((filter: ColFilter) => filter.column === col);
    let tmpFilter = colFilters;
    if (colIndex >= 0) {
      // filter exists --> overwrite
      tmpFilter[colIndex] = { column: col, filter: filters } as ColFilter;
    } else {
      // create new
      tmpFilter = [...colFilters, { column: col, filter: filters } as ColFilter];
    }
    let tmpData = data;
    // filter data by all column filters
    tmpFilter.map((f: ColFilter) => {
      // no selected filter keys on column
      if (f.filter.length === 0) return tmpData;
      tmpData = tmpData && tmpData.filter((item: any) => f.filter.includes(item[f.column]));
    });
    if (!isInitial) {
      setFilteredData(tmpData);
    } else {
      return tmpData;
    }
    setColFilters(tmpFilter);
  };

  useEffect(() => {
    if (!columnFilters) return;
    let _data = data;
    columns.map((c: TableColumn) => {
      if (c.hideFilter) return;
      const filter = window.sessionStorage.getItem(
        sessionStorageKeys.TABLE_COL_FILTER + c.accessor,
      );
      const parsed = filter && filter !== '[]' && JSON.parse(filter);
      if (!parsed) return;
      _data = applyFilters(_data, c.accessor, parsed, true);
    });
    setFilteredData(_data);
  }, [data, colFilters]);

  /**
   * Filters data by selected filters
   * used as options for filtering column
   * @param column
   */
  const getUniqueDataForColumn = (column: any, _innerAccesor?: string) => {
    if (!data) return;
    let filteredColData: string[] = [];
    data.map((x: any) => {
      if (_innerAccesor) {
        if (!_innerAccesor) return;
        const obj = x[column];
        if (obj && obj[_innerAccesor] && !filteredColData.includes(obj[_innerAccesor])) {
          filteredColData = [...filteredColData, obj[_innerAccesor]];
        }
      } else {
        if (!filteredColData.includes(x[column])) {
          filteredColData = [...filteredColData, x[column]];
        }
      }
    });
    return filteredColData;
  };

  /**
   * Search by query in every column of table
   * @param query
   */
  const handleSearchByQuery = (query: string) => {
    if (!data) return;
    const displayedColumns = columns.map((col: TableColumn) => col.accessor);
    const _data = searchByQuery(query, data, displayedColumns);
    setFilteredData(_data);
  };

  /**
   * Selects row or calls onSelected function on selected row
   * @param item
   * @param rowIdx
   */
  const handleSelectItem = (item: any, rowIdx: number) => {
    if (!onSelected || !item || !filteredData) return;
    if (selectedRowIndex === rowIdx) {
      // row is already selected --> call onSelected
      onSelected(filteredData[rowIdx]);
    } else {
      // select row
      setSelectedRowIndex(rowIdx);
    }
  };

  const baseBtnStyle = `mr-3 py-1 px-1 text-xs`;
  return (
    <>
      <div
        id="data-table"
        className={`overflow-x-auto ${isModalTable ? `` : `shadow-md`} sm:rounded-lg`}
      >
        <div className="overflow-x">
          {fulltextSearch && (
            <input
              placeholder={t('general.search')}
              type="text"
              onChange={(e) => handleSearchByQuery(e.target.value)}
              className="block m-auto p-2 text-sm text-gray-900 rounded-md border border-gray-600 focus:outline-none focus:ring-0 focus:border-gray-600"
            />
          )}
          <table tabIndex={0} className="w-full text-sm text-left outline-0" ref={tableRef}>
            <thead className="text-xs text-gray-700 uppercase">
              <tr>
                {columns.map((col, idx) => (
                  <th className={`py-3 px-2 ${col.className}`} scope="col" key={idx}>
                    <span className={col.hideFilter ? undefined : `flex items-center`}>
                      <span className={col.className}>{t(col.name)}</span>
                      {/* Filters modal */}
                      {data && columnFilters && !col.hideFilter && (
                        <span className="normal-case">
                          <SelectMultiple
                            colId={col.accessor}
                            data={getUniqueDataForColumn(col.accessor, col?.innerAccesor) ?? []}
                            onSubmit={(e) => applyFilters(data, col.accessor, e)}
                            translateEnum={col.translateEnum}
                            icon={
                              <ViewColumnsIcon
                                className="h-4 ml-3 inline"
                                onClick={() => showFilters(col.accessor)}
                              />
                            }
                          />
                        </span>
                      )}
                    </span>
                  </th>
                ))}
                {(handleEdit || handleRemove || customButtons) && (
                  <th className="py-3 px-3" scope="col"></th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredData === null && (
                <tr>
                  <td colSpan={columns.length} className="p-5 text-center">
                    <Loader loading isTableLoading />
                  </td>
                </tr>
              )}
              {filteredData?.length === 0 && (
                <tr className="min-h-fit">
                  <td colSpan={columns.length} className="p-5 text-center leading-10">
                    {t('general.no-data')}
                  </td>
                </tr>
              )}
              {filteredData?.map((row, rowIdx: number) => {
                return (
                  <React.Fragment key={rowIdx}>
                    {/* heading above specified row */}
                    {aboveRowHeading &&
                      aboveRowHeading.map((item, index) =>
                        item.rowIdx === rowIdx ? <tr key={index}>{item.heading}</tr> : null,
                      )}
                    <tr
                      id={`row-${rowIdx}`}
                      onClick={() => handleSelectItem(filteredData[rowIdx], rowIdx)}
                      className={`${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-100'} border-b ${
                        rowIdx % 2 === 0 ? 'dark:bg-gray-200' : 'dark:bg-white'
                      } dark:border-gray-200 overflow-x-scroll ${
                        selectedRowIndex === rowIdx && `!border-2 border-dashed	!border-blue-300`
                      }`}
                    >
                      {columns.map((col, colIdx) => (
                        <td
                          className={`${density === DensityType.COMPACT ? `py-1` : `py-4`} px-2 ${
                            col.className
                          }`}
                          key={colIdx}
                        >
                          {col.cellRenderer
                            ? col.cellRenderer(row)
                            : handleFormatCell(row[col.accessor], col)}
                        </td>
                      ))}
                      {(handleEdit || handleRemove || customButtons) && (
                        <td
                          className={`flex !items-center justify-end ${
                            density === DensityType.COMPACT ? `py-0` : `py-2`
                          }`}
                        >
                          <span onContextMenu={(e) => showContextMenu && handleContextMenu(e, row)}>
                            {/* <ContextMenu /> */}
                            {handleEdit && (
                              <Button
                                className={`order-first ${baseBtnStyle}`}
                                title={t('general.edit')}
                                onClick={() => handleEdit(row[editIdKey ?? idKey])}
                                color="blue"
                              />
                            )}
                            {customButtons &&
                              customButtons.map((btn: CustomButton, i: number) => {
                                /* dont render button if condition is false */
                                if (
                                  btn.renderCondition &&
                                  btn.renderCondition &&
                                  btn.renderCondition(row['id']) === false
                                )
                                  return;
                                return (
                                  <Button
                                    key={i}
                                    className={`${baseBtnStyle} ${btn.className ?? ``}`}
                                    color={btn.color ?? 'blue'}
                                    title={btn.title}
                                    icon={btn.icon}
                                    showLoading={btn.showLoading}
                                    onClick={() => btn.onSubmit(row[idKey])}
                                  />
                                );
                              })}
                            {handleRemove && (
                              <Button
                                className={`order-last ${baseBtnStyle}`}
                                color="red"
                                title={t('general.remove')}
                                onClick={() => removeAction(row[removeIdKey ?? idKey])}
                              />
                            )}
                          </span>
                        </td>
                      )}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Delete modal */}
        <Transition appear show={deleteModalVisible} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={handleDeleteModalClose}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-red-500">
                      {t('general.remove-headline')}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{t('general.remove-confirm')}</p>
                    </div>

                    <div className="mt-4">
                      <Button
                        type="button"
                        color="red"
                        onClick={() => {
                          handleRemove && selectedItem && handleRemove(selectedItem);
                          handleDeleteModalClose();
                        }}
                        title={t('general.remove')}
                        className="mr-3"
                      />
                      <Button
                        type="button"
                        onClick={() => {
                          handleDeleteModalClose();
                        }}
                        title={t('general.close')}
                        color={'gray'}
                      />
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </>
  );
}
